import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  ChipProps,
  alpha,
  Paper,
  LinearProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Link as RouterLink, useLocation } from "react-router-dom";

/* Apollo + GQL */
import { useMutation, useQuery } from "@apollo/client";
import { SHARE_PROVIDER_CONTACT } from "../../../../platform/graphql/mutations/care";
import { GET_INSTALLMENTS_BY_LEAD } from "../../../../platform/graphql/queries/care";
import { GET_BOOKINGS, GET_BOOKINGS_BY_EMAIL } from "../../../../platform/graphql/queries/booking";
import {
  MY_PENDING_BROADCAST_INVITES,
  PROVIDER_RESPOND_INVITE,
} from "./broadcastInvitesPanel";
import {
  CLOSED_LEAD_STATUSES,
  ConciergeBannerForProvider,
  deriveAgentFromExtra,
  formatTourScheduleLabel,
  getLatestTourCancellation,
  getInvitePayerType,
  getReferralCardAccent,
  getResolvedBookedTour,
  NEEDS_ACTION_STATUSES,
  normalizePayerType,
  PENDING_INVITE_STATUSES,
  parseBroadcastSeed,
  parseExtraLoose,
  ProviderInvite,
} from "./incomingReferralsShared";

/* Helpers */
import {
  getResidentLabel,
  mapUrgency,
  mapPayerType,
  mapKind,
  mapCareNeed,
  getCareNeedRaw,
  getSettlementMeta,
  getPricingFlagsFromExtra,
  getReferrerPaidTotal,
  formatUSD,
} from "../../../../platform/careComponents/helper/referralHelpers";

import InstallmentsInline from "../../../../platform/careComponents/components/InstallmentsInline";
import { ChipWrap, DateStamp } from "../../../../platform/careComponents/helper/sharedFun";
import ProviderLeadChatDialog from "../../../../platform/careComponents/components/providerLeadChatDialog";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { isOpenStatus, LeadStatus, statusToChipColor } from "../../../../platform/careComponents/utils/statuses";
/* =============================================================================
   Types
   ============================================================================= */
export type IncomingReferralsSectionProps = {
  leads: any[];
  leadsCount: number;
  leadLoading: boolean;
  leadError: any;
  businessId?: number | null;
  businessName?: string | null;
  refetchLeads: () => void;
  onBroadcastInviteCountChange?: (count: number) => void;

  onOpenSettle: (lead: any) => void;
  onCancelLead: (lead: any, ev?: React.MouseEvent) => void;
  onBeginInstallmentPay: (args: {
    installmentId: string;
    amountDue: number;
  }) => void;
  cancelLoading?: boolean;

  /** ✅ NEW: If provided, we’ll show a “Manage Availability” button in header */
  /** Provider-owned URL; Care must not link directly to Timely UI routes. */
  availabilityManagementUrl?: string | null;
  panelMode?: boolean;
};

type ReferralItemAccordionProps = {
  lead: any;
  businessId?: string | number | null;
  businessName?: string | null;
  onOpenSettle: (lead: any) => void;
  onCancelLead: (lead: any, ev?: React.MouseEvent) => void;
  onAcceptNoAgreement?: (leadId: string | number) => void;
  onBeginInstallmentPay: (args: {
    installmentId: string;
    amountDue: number;
  }) => void;
  cancelLoading?: boolean;
  acceptingNoAgreement?: boolean;
};
/* =============================================================================
   Hook: compute totals from referralInstallments only (no refereeSettlementAmt)
   ============================================================================= */
function useInstallmentTotals(leadId: string | number) {
  // we only need `data` + `loading`
  const { data, loading } = useQuery(GET_INSTALLMENTS_BY_LEAD, {
    variables: { leadId: String(leadId) },
    fetchPolicy: "cache-first",
  });

  type Row = {
    dueAmount?: number | string;
    paidAmount?: number | string;
    status?: string;
  };
  const rows: Row[] = (data?.referralInstallments ?? []) as Row[];

  const total = rows.reduce((s, r) => s + Number(r?.dueAmount || 0), 0);
  const paid = rows.reduce((s, r) => s + Number(r?.paidAmount || 0), 0);
  const remaining = Math.max(0, total - paid);
  const hasSchedule = rows.length > 0;

  const allPaid = hasSchedule
    ? rows.every(
      (r) =>
        Number(r?.paidAmount || 0) >= Number(r?.dueAmount || 0) ||
        r?.status === "PAID"
    )
    : false;

  return { loading, total, paid, remaining, hasSchedule, allPaid };
}

/* =============================================================================
   Helper: decide display status from installments only for PLACED
   ============================================================================= */
function getDisplayStatusFromInstallments(
  baseStatus: string,
  it: {
    loading: boolean;
    hasSchedule: boolean;
    allPaid: boolean;
    paid: number;
    remaining: number;
  }
) {
  // Only augment when lead is PLACED and a schedule exists
  if (baseStatus === "PLACED" && it.hasSchedule) {
    if (it.loading) return "Placed • Checking…";
    if (it.allPaid || it.remaining <= 0) return "Settled";
    if (it.paid > 0) return "Payment Incomplete";
    return "Payment Pending";
  }
  if (baseStatus === "PLACED") {
    return "Settled";
  }
  return baseStatus;
}

/* =============================================================================
   Helper: show “Referrer-Reported Settlement” badge pre-payment
   ============================================================================= */
function ReferrerReportedNote({
  leadId,
  settledAt,
  refereeSettlementAmt,
}: {
  leadId: string | number;
  settledAt?: string | null;
  refereeSettlementAmt: number;
}) {
  const { data } = useQuery(GET_INSTALLMENTS_BY_LEAD, {
    variables: { leadId: String(leadId) },
    fetchPolicy: "cache-first",
  });

  type InstallmentStatus = "DUE" | "PAID" | "CANCELLED" | "FAILED";
  type Row = {
    status: InstallmentStatus;
    dueAmount: number;
    paidAmount: number;
  };

  const rows: Row[] = (data?.referralInstallments ?? []) as Row[];
  const hasSchedule = rows.length > 0;

  const providerHasStartedPay = rows.some(
    (r) => Number(r?.paidAmount || 0) > 0 || r?.status === "PAID"
  );

  const allPaid = hasSchedule
    ? rows.every(
      (r) =>
        r.status === "PAID" ||
        Number(r?.paidAmount || 0) >= Number(r?.dueAmount || 0)
    )
    : false;

  const showSettled = !!settledAt && (!hasSchedule || allPaid);

  const show =
    Number(refereeSettlementAmt) > 0 && !showSettled && !providerHasStartedPay;

  if (!show) return null;

  return (
    <Tooltip title="Referrer reported settlement. Complete settlement to close the beds.">
      <Chip
        size="small"
        color="warning"
        variant="filled"
        label="Referrer-Reported Settlement"
      />
    </Tooltip>
  );
}

/* =============================================================================
   Per-Lead Accordion
   ============================================================================= */
function ReferralItemAccordion({
  lead: l,
  businessId,
  businessName,
  onOpenSettle,
  onCancelLead,
  onAcceptNoAgreement,
  onBeginInstallmentPay,
  cancelLoading,
  acceptingNoAgreement = false,
}: ReferralItemAccordionProps) {
  const isProd = process.env.NODE_ENV === "production";
  const [isPageVisible, setIsPageVisible] = React.useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible"
  );
  const bookingLookupPollMs = isPageVisible ? (isProd ? 30000 : 15000) : 0;
  const location = useLocation();
  const [chatOpen, setChatOpen] = React.useState(false);
  const contactStatus: string =
    l?.providerContact?.status ?? "PENDING_PROVIDER";
  const contactUnlocked = contactStatus === "UNLOCKED";

  // Route to the agreement page with referral context.
  const agreementUrl = `/care/agreement?lead=${encodeURIComponent(
    l.id
  )}&business=${encodeURIComponent(l.businessId)}&returnTo=${encodeURIComponent(
    location.pathname + location.search
  )}`;

  /* --------- Derivations --------- */
  const resident = getResidentLabel(l);
  const kindLabel = mapKind(l?.kind);
  const rawSourceType = String(l?.referralSourceTypes?.code || "").trim().toUpperCase();
  const normalizedPayerType = React.useMemo(
    () => normalizePayerType(l?.payerType),
    [l?.payerType],
  );
  const sourceType =
    rawSourceType === "AGENT_SOURCED"
      ? "Agent Referral"
      : normalizedPayerType === "PRIVATE"
        ? "Direct Private Pay Referral"
        : "Direct Referral";
  const refName = l?.referrer?.contactName || "—";

  const { holdExpiresAt, settledAt, feePerBed, bedCount } = getSettlementMeta(
    l.extra
  );
  const pricingFlags = getPricingFlagsFromExtra(l.extra);
  const refPaid = getReferrerPaidTotal(l);

  const requiresAgentInvite = pricingFlags.mode === "CONCIERGE";
  // Direct-to-provider private leads can be accepted immediately.
  // Concierge leads still wait until the provider has actually been invited.
  const canAcceptNow = !requiresAgentInvite || contactStatus === "INVITED";

  const refereeSettlementAmt = React.useMemo(() => {
    const n = Number(l?.settlementTotalByReferrer);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [l?.settlementTotalByReferrer]);
  const referrerEmail = React.useMemo(
    () =>
      String(
        l?.referrer?.email ||
        l?.referrerContact?.email ||
        parseExtraLoose(l?.extra)?.referrerContact?.email ||
        ""
      ).trim(),
    [l],
  );
  const referrerBookingsQuery = useQuery(GET_BOOKINGS_BY_EMAIL, {
    variables: {
      email: referrerEmail,
      selectedYear: new Date().getFullYear(),
    },
    skip: !referrerEmail,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    pollInterval: referrerEmail ? bookingLookupPollMs : 0,
  });
  const resolvedBusinessId = Number(businessId);
  const businessBookingsQuery = useQuery(GET_BOOKINGS, {
    variables: {
      businessId: resolvedBusinessId,
      selectedYear: new Date().getFullYear(),
    },
    skip: !businessId || !Number.isFinite(resolvedBusinessId),
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    pollInterval:
      businessId && Number.isFinite(resolvedBusinessId) ? bookingLookupPollMs : 0,
  });
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  const referrerBookings = React.useMemo(
    () => (referrerBookingsQuery.data?.getBookingsByEmail ?? []) as any[],
    [referrerBookingsQuery.data?.getBookingsByEmail],
  );
  const businessBookings = React.useMemo(
    () => (businessBookingsQuery.data?.getBookings ?? []) as any[],
    [businessBookingsQuery.data?.getBookings],
  );
  const bookingPool = React.useMemo(() => {
    const merged = new Map<string, any>();
    [...referrerBookings, ...businessBookings].forEach((booking: any) => {
      const key =
        String(booking?.id || "").trim() ||
        String(booking?.bookingId || "").trim() ||
        `${String(booking?.time || "").trim()}::${String(booking?.inviteeEmail || "").trim().toLowerCase()}`;
      if (!key || merged.has(key)) return;
      merged.set(key, booking);
    });
    return Array.from(merged.values());
  }, [businessBookings, referrerBookings]);
  const requiresReferralAgreement = normalizedPayerType === "PRIVATE";
  const bookedTour = React.useMemo(
    () =>
      getResolvedBookedTour(l, bookingPool, businessName),
    [bookingPool, businessName, l],
  );
  const bookedTourForDisplay = React.useMemo(() => {
    const rawStatus = String(l?.status || "").toUpperCase();
    if (CLOSED_LEAD_STATUSES.has(rawStatus)) return null;
    return bookedTour;
  }, [bookedTour, l?.status]);
  const latestTourCancellation = React.useMemo(
    () => (bookedTourForDisplay ? null : getLatestTourCancellation(l)),
    [bookedTourForDisplay, l]
  );
  const bookedTourLabel = React.useMemo(
    () => formatTourScheduleLabel(bookedTourForDisplay?.bookingTime),
    [bookedTourForDisplay?.bookingTime],
  );

  // 🔸 Installment totals for THIS LEAD (does NOT use refereeSettlementAmt)
  const it = useInstallmentTotals(l.id);
  const resolvedStatus = React.useMemo(() => {
    const rawStatus = String(l?.status || "").toUpperCase();
    if (CLOSED_LEAD_STATUSES.has(rawStatus)) return rawStatus;
    if (bookedTourForDisplay) return "TOUR_SCHEDULED";
    if (rawStatus === "TOUR_SCHEDULED") {
      return contactUnlocked ? "QUALIFYING" : "NEW";
    }
    return rawStatus || "NEW";
  }, [bookedTourForDisplay, contactUnlocked, l?.status]);
  const isActiveStage = ["NEW", "QUALIFYING", "MATCHING", "TOUR_SCHEDULED"].includes(
    resolvedStatus
  );
  const displayStatus = getDisplayStatusFromInstallments(resolvedStatus, it);

  // Optional: component-level overrides (keeps your previous NEW → warning)
  const LOCAL_OVERRIDES: Partial<Record<LeadStatus, ChipProps["color"]>> = {
    NEW: "warning",
  };

  // Helper: base color with local override, then library fallback
  const baseStatusColor = (status: string): ChipProps["color"] =>
    (LOCAL_OVERRIDES as Record<string, ChipProps["color"]>)[status] ??
    statusToChipColor(status);

  // 🔸 Final color logic
  // - If PLACED and has a schedule: warning when remaining > 0, success when settled
  // - Otherwise use base color (local override -> centralized map)
  const chipColor: ChipProps["color"] =
    resolvedStatus === "PLACED"
      ? it.hasSchedule
        ? it.remaining > 0
          ? "warning"
          : "success"
        : "success"
      : baseStatusColor(resolvedStatus);

  // Concierge agent (if any) from extra
  const agent = React.useMemo(() => deriveAgentFromExtra(l.extra), [l.extra]);
  const cardTone = getReferralCardAccent(resolvedStatus);

  return (
    <Accordion
      defaultExpanded={resolvedStatus === "NEW"}
      disableGutters
      sx={{
        border: 1,
        borderColor: cardTone.border,
        borderRadius: 3.5,
        borderLeftWidth: 6,
        borderLeftColor: cardTone.accent,
        background: cardTone.bodyBg,
        boxShadow: "0 14px 28px rgba(15, 31, 46, 0.06)",
        "&:before": { display: "none" },
        "& + &": { mt: 1.5 },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 82,
          px: { xs: 1.5, sm: 2 },
          py: 0.4,
          bgcolor: cardTone.headerBg,
          alignItems: "flex-start",
          borderBottom: "1px solid rgba(103, 114, 126, 0.16)",
          "&.Mui-expanded": {
            minHeight: 82,
          },
          "& .MuiAccordionSummary-content": {
            my: 1.1,
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            mt: 0.5,
          },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1, md: 2 }}
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ width: "100%" }}
        >
          <ChipWrap>
            <Chip size="small" label={kindLabel} variant="outlined" />

            {/* ✅ Status chip augmented by installments-only logic */}
            <Chip
              size="small"
              label={displayStatus}
              color={chipColor}
              variant={isActiveStage ? "outlined" : "filled"}
            />
            {bookedTourForDisplay && (
              <Chip
                size="small"
                icon={<EventAvailableIcon />}
                color="success"
                variant="outlined"
                label={
                  bookedTourLabel
                    ? `Tour Scheduled: ${bookedTourLabel}`
                    : "Tour Scheduled"
                }
              />
            )}

            {!!holdExpiresAt && (
              <Chip
                size="small"
                label="Hold • Expires"
                color="warning"
                variant="outlined"
              />
            )}

            {/* ✅ Your original chips preserved */}
            {refPaid > 0 && (
              <Tooltip title="Referrer indicates payment made externally — complete settlement here to close the beds.">
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`Referrer Paid ${formatUSD(refPaid)}`}
                />
              </Tooltip>
            )}

            <ReferrerReportedNote
              leadId={l.id}
              settledAt={settledAt}
              refereeSettlementAmt={refereeSettlementAmt}
            />
          </ChipWrap>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: "0.98rem" }}
            >
              {refName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.2 }}
            >
              Resident: {resident} • Source: {sourceType}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} sx={{ minWidth: 220 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Created
              </Typography>
              <DateStamp value={l.createdAt} />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Desired
              </Typography>
              <DateStamp value={l.desiredDate || null} />
            </Box>
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.75, sm: 2 },
          bgcolor: cardTone.bodyBg,
        }}
      >
        <Stack spacing={1.5}>
          {/* Concierge banner for providers (only while open) */}
          {pricingFlags.mode === "CONCIERGE" && isOpenStatus(resolvedStatus) && (
            <ConciergeBannerForProvider
              isRush={!!pricingFlags.isRush}
              contactUnlocked={contactUnlocked}
              agent={agent}
              refName={l?.referrer?.contactName || null}
              bookedTour={bookedTourForDisplay}
            />
          )}

          {bookedTourForDisplay && (
            <Alert severity="success" icon={<EventAvailableIcon fontSize="inherit" />}>
              {bookedTourLabel
                ? `Tour scheduled for ${bookedTourLabel}${bookedTourForDisplay.businessName ? ` with ${bookedTourForDisplay.businessName}` : ""}.`
                : `Tour already scheduled${bookedTourForDisplay.businessName ? ` with ${bookedTourForDisplay.businessName}` : ""}.`}
            </Alert>
          )}
          {latestTourCancellation && (
            <Alert severity="info">
              {`The scheduled tour${latestTourCancellation.bookingTime
                  ? ` for ${formatTourScheduleLabel(latestTourCancellation.bookingTime)}`
                  : ""
                }${latestTourCancellation.businessName
                  ? ` with ${latestTourCancellation.businessName}`
                  : ""
                } was canceled${latestTourCancellation.canceledByName
                  ? ` by ${latestTourCancellation.canceledByName}`
                  : latestTourCancellation.canceledByRole
                    ? ` by ${latestTourCancellation.canceledByRole.toLowerCase()}`
                    : ""
                }.`}
            </Alert>
          )}

          {/* PLACED panel & inline installments list */}
          {l.status === "PLACED" && (
            <Box
              sx={{
                p: 1.2,
                borderRadius: 2.5,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
              }}
            >
              <InstallmentsInline
                leadId={l.id}
                onBeginPay={({ installmentId, amountDue }) =>
                  onBeginInstallmentPay({ installmentId, amountDue })
                }
              />
            </Box>
          )}

          {/* Key attributes row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              gap: 1.25,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Care Need
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {mapCareNeed(getCareNeedRaw(l))}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Urgency
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {mapUrgency(l.urgency)}
              </Typography>
              <ChipWrap>
                <Chip
                  size="small"
                  label={
                    pricingFlags.mode === "CONCIERGE"
                      ? "Concierge"
                      : normalizedPayerType === "PRIVATE"
                        ? "Direct Private Pay"
                        : "Direct"
                  }
                  variant="outlined"
                  color={
                    pricingFlags.mode === "CONCIERGE" ? "secondary" : "default"
                  }
                />
                {pricingFlags.isRush && (
                  <Chip
                    size="small"
                    label="Rush"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </ChipWrap>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Payer
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {mapPayerType(l.payerType)}
              </Typography>
            </Box>

            {(Number.isFinite(feePerBed) || bedCount) && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Quote
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {Number.isFinite(feePerBed)
                    ? `${feePerBed?.toLocaleString()}/bed`
                    : "—"}
                  {bedCount ? ` • ${bedCount} bed(s)` : ""}
                </Typography>
              </Box>
            )}
          </Box>

          {/* CTA row (concierge agent-gated) */}
          {isActiveStage && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="flex-end"
              sx={{
                "& .MuiButton-root": {
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 600,
                  minHeight: 38,
                },
              }}
            >
              {contactUnlocked ? (
                // ✅ Already accepted/unlocked
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ width: "100%" }}
                >
                  <Alert
                    icon={<CheckCircleRoundedIcon fontSize="small" />}
                    severity="success"
                    sx={{ flex: 1 }}
                  >
                    {requiresReferralAgreement ? (
                      <>
                        Contact shared with the referrer. By accepting, you agreed
                        to the success-fee terms for this referral.
                        <Button
                          component={RouterLink}
                          to={`/care/agreement?lead=${encodeURIComponent(
                            l.id
                          )}&business=${encodeURIComponent(l.businessId)}`}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          View agreement
                        </Button>
                      </>
                    ) : (
                      <>
                        No referral agreement is required for {mapPayerType(normalizedPayerType)} referrals.
                        The referrer will contact you directly.
                      </>
                    )}
                  </Alert>

                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onOpenSettle(l)}
                      sx={{ minWidth: 140 }}
                    >
                      Mark Settled
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={(e) => onCancelLead(l, e)}
                      disabled={cancelLoading}
                      sx={{ minWidth: 140 }}
                    >
                      Reject Referral
                    </Button>
                  </Stack>
                </Stack>
              ) : !requiresReferralAgreement ? (
                <>
                  <Alert severity="info" sx={{ flex: 1 }}>
                    No referral agreement is required for {mapPayerType(normalizedPayerType)} referrals.
                    The referrer will contact you directly.
                  </Alert>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onAcceptNoAgreement?.(String(l.id))}
                    disabled={acceptingNoAgreement}
                    sx={{ minWidth: 160 }}
                  >
                    {acceptingNoAgreement ? "Accepting..." : "Accept Request"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={(e) => onCancelLead(l, e)}
                    disabled={cancelLoading}
                    sx={{ minWidth: 160 }}
                  >
                    Reject Referral
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onOpenSettle(l)}
                    sx={{ minWidth: 140 }}
                  >
                    Mark Settled
                  </Button>
                </>
              ) : canAcceptNow ? (
                // Accept request and agreement together
                <>
                  <Button
                    size="small"
                    variant="contained"
                    component={RouterLink}
                    to={agreementUrl}
                    sx={{ minWidth: 260 }}
                  >
                    Review Request and Agreement
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={(e) => onCancelLead(l, e)}
                    disabled={cancelLoading}
                    sx={{ minWidth: 160 }}
                  >
                    Reject Referral
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onOpenSettle(l)}
                    sx={{ minWidth: 140 }}
                  >
                    Mark Settled
                  </Button>
                </>
              ) : (
                // Concierge lead exists but is not ready on the provider side yet
                <>
                  <Tooltip title="Your placement specialist must send this concierge request before you can accept it.">
                    {/* Tooltip needs an element wrapper when Button is disabled */}
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        disabled
                        sx={{ minWidth: 260 }}
                      >
                        Review Request and Agreement
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={(e) => onCancelLead(l, e)}
                    disabled={cancelLoading}
                    sx={{ minWidth: 160 }}
                  >
                    Reject Referral
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onOpenSettle(l)}
                    sx={{ minWidth: 140 }}
                  >
                    Mark Settled
                  </Button>
                </>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<QuestionAnswerIcon />}
                onClick={() => setChatOpen(true)}
                sx={{ minWidth: 140 }}
              >
                Chat Agent
              </Button>
              {/* Chat dialog, scoped to THIS lead */}
              <ProviderLeadChatDialog
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                leadId={l.id}
                providerName={l?.business?.name || null}
              />
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function ProviderInviteRespondDialog({
  open,
  action,
  businessId,
  onClose,
  onSubmit,
}: {
  open: boolean;
  action: "ACCEPTED" | "DECLINED" | null;
  businessId?: string | null;
  onClose: () => void;
  onSubmit: (note: string, agreementAccepted: boolean) => void;
}) {
  const [note, setNote] = React.useState("");
  const [agreementAccepted, setAgreementAccepted] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setNote("");
      setAgreementAccepted(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {action === "ACCEPTED" ? "Review Request and Agreement" : "Decline Request"}
      </DialogTitle>
      <DialogContent>
        {action === "ACCEPTED" ? (
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Review this request and the Plany365 Care Provider Service Agreement for this case
            before final acceptance.
            {businessId ? (
              <Button
                component={RouterLink}
                to={`/care/agreement?businessId=${encodeURIComponent(String(businessId))}`}
                target="_blank"
                rel="noreferrer"
                size="small"
                sx={{ ml: 1 }}
              >
                View full agreement
              </Button>
            ) : null}
          </Alert>
        ) : null}
        <TextField
          fullWidth
          multiline
          minRows={3}
          sx={{ mt: 1 }}
          label="Optional note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add availability details, requirements, or timing."
        />
        {action === "ACCEPTED" ? (
          <Box sx={{ mt: 2, display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Checkbox
              sx={{ mt: -0.75 }}
              checked={agreementAccepted}
              onChange={(e) => setAgreementAccepted(e.target.checked)}
            />
            <Typography variant="body2">
              I have reviewed and agree to both this request and the Plany365 Care Provider
              Service Agreement.
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(note, agreementAccepted)}
          disabled={action === "ACCEPTED" && !agreementAccepted}
        >
          {action === "ACCEPTED" ? "Accept After Review" : "Decline"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ProviderInviteCard({
  invite,
  responding,
  onAccept,
  onDecline,
}: {
  invite: ProviderInvite;
  responding: boolean;
  onAccept: (inviteId: string, requiresAgreement: boolean, businessId?: string | null) => void;
  onDecline: (inviteId: string, businessId?: string | null) => void;
}) {
  const location = useLocation();
  const seed = React.useMemo(
    () => parseBroadcastSeed(invite?.group?.seedJson),
    [invite?.group?.seedJson],
  );
  const inviteStatus = String(invite?.status || "").toUpperCase();
  const isDraftInvite = inviteStatus === "DRAFT";
  const requiresAgreement = getInvitePayerType(invite) === "PRIVATE";
  const agreementUrl = React.useMemo(() => {
    const params = new URLSearchParams();
    params.set("invite", String(invite.id));
    if (invite.business?.id != null) {
      params.set("businessId", String(invite.business.id));
    }
    params.set("returnTo", location.pathname + location.search);
    return `/care/agreement?${params.toString()}`;
  }, [invite.business?.id, invite.id, location.pathname, location.search]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3.5,
        borderColor: (t) => alpha(t.palette.warning.main, 0.34),
        borderLeft: (t) => `6px solid ${alpha(t.palette.warning.main, 0.74)}`,
        bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
        boxShadow: "0 14px 28px rgba(15, 31, 46, 0.06)",
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" fontWeight={700}>
            New Request
          </Typography>
          <Chip
            size="small"
            color={isDraftInvite ? "info" : "warning"}
            label={isDraftInvite ? "Private Request" : "Needs Response"}
          />
          {invite?.group?.createdAt ? (
            <Chip
              size="small"
              variant="outlined"
              label={<DateStamp value={invite.group.createdAt} />}
              sx={{
                height: "auto",
                alignItems: "flex-start",
                "& .MuiChip-label": {
                  display: "block",
                  whiteSpace: "normal",
                  paddingTop: 0.75,
                  paddingBottom: 0.75,
                },
              }}
            />
          ) : null}
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {seed.residentInitials ? (
            <Chip size="small" label={`Resident: ${seed.residentInitials}`} />
          ) : null}
          {seed.careNeed ? (
            <Chip size="small" label={`Care: ${seed.careNeed}`} />
          ) : null}
          {seed.payerType ? (
            <Chip size="small" label={`Payer: ${seed.payerType}`} />
          ) : null}
          {seed.urgency ? (
            <Chip size="small" label={`Urgency: ${seed.urgency}`} />
          ) : null}
        </Stack>

        {seed.notes ? (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            <strong>Notes: </strong>
            {seed.notes}
          </Typography>
        ) : null}

        {isDraftInvite ? (
          <Alert severity="info">
            This private request was sent by a concierge agent. You can accept or decline it here.
          </Alert>
        ) : null}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            size="small"
            variant="contained"
            component={requiresAgreement ? RouterLink : "button"}
            to={requiresAgreement ? agreementUrl : undefined}
            onClick={
              requiresAgreement
                ? undefined
                : () =>
                  onAccept(
                    invite.id,
                    false,
                    invite.business?.id != null ? String(invite.business.id) : null
                  )
            }
            disabled={responding}
          >
            {requiresAgreement ? "Review Request and Agreement" : "Accept Request"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() =>
              onDecline(
                invite.id,
                invite.business?.id != null ? String(invite.business.id) : null
              )
            }
            disabled={responding}
          >
            Decline
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/* =============================================================================
   Section Wrapper
   ============================================================================= */

export default function IncomingReferralsSection({
  leads = [],
  leadsCount = 0,
  leadLoading,
  leadError,
  businessId = null,
  businessName = null,
  refetchLeads,
  onOpenSettle,
  onCancelLead,
  onBeginInstallmentPay,
  cancelLoading,
  availabilityManagementUrl = null,
  onBroadcastInviteCountChange,
  panelMode = false,
}: IncomingReferralsSectionProps) {
  const isProd = process.env.NODE_ENV === "production";
  const [isPageVisible, setIsPageVisible] = React.useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible"
  );
  const livePollMs = isPageVisible ? (isProd ? 30000 : 15000) : 0;
  const PAGE_SIZE = 12;
  const [payerTab, setPayerTab] = React.useState<"private" | "non_private" | "all">("private");
  const [viewFilter, setViewFilter] = React.useState<
    "needs_action" | "new" | "placed" | "closed" | "all"
  >("needs_action");
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  const [respondDialog, setRespondDialog] = React.useState<{
    inviteId: string | null;
    action: "ACCEPTED" | "DECLINED" | null;
    businessId: string | null;
  }>({ inviteId: null, action: null, businessId: null });

  const {
    data: inviteData,
    loading: inviteLoading,
    error: inviteError,
    refetch: refetchInvites,
  } = useQuery<{ myReferralInvites: ProviderInvite[] }>(
    MY_PENDING_BROADCAST_INVITES,
    {
      variables: {
        businessId,
        statuses: [...PENDING_INVITE_STATUSES],
      },
      skip: !businessId,
      fetchPolicy: "cache-and-network",
      pollInterval: businessId ? livePollMs : 0,
    }
  );

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const providerInvites = React.useMemo(
    () => (inviteData?.myReferralInvites ?? []) as ProviderInvite[],
    [inviteData?.myReferralInvites]
  );

  React.useEffect(() => {
    onBroadcastInviteCountChange?.(providerInvites.length);
  }, [onBroadcastInviteCountChange, providerInvites.length]);

  const [respondToInvite, { loading: respondingInvite, error: respondInviteError }] =
    useMutation(PROVIDER_RESPOND_INVITE, {
      onCompleted: async () => {
        await Promise.all([refetchInvites(), refetchLeads()]);
      },
    });
  const [shareProviderContact, { loading: sharingProviderContact }] = useMutation(
    SHARE_PROVIDER_CONTACT,
    {
      onCompleted: async () => {
        await refetchLeads();
      },
    }
  );

  const sortedLeads = React.useMemo(() => {
    const toMs = (v: any) => {
      const t = Date.parse(String(v || ""));
      return Number.isFinite(t) ? t : 0;
    };
    return [...(Array.isArray(leads) ? leads : [])].sort((a: any, b: any) => {
      const pa = NEEDS_ACTION_STATUSES.has(String(a?.status || "").toUpperCase()) ? 1 : 0;
      const pb = NEEDS_ACTION_STATUSES.has(String(b?.status || "").toUpperCase()) ? 1 : 0;
      if (pa !== pb) return pb - pa;
      return toMs(b?.updatedAt || b?.createdAt) - toMs(a?.updatedAt || a?.createdAt);
    });
  }, [leads]);

  const counts = React.useMemo(() => {
    const all = sortedLeads.length + providerInvites.length;
    const privateCount =
      sortedLeads.filter(
        (l: any) => normalizePayerType(l?.payerType) === "PRIVATE"
      ).length +
      providerInvites.filter((invite) => getInvitePayerType(invite) === "PRIVATE").length;
    const normalizedNonPrivateCount = all - privateCount;
    return { all, privateCount, nonPrivateCount: normalizedNonPrivateCount };
  }, [providerInvites, sortedLeads]);

  const payerScopedLeads = React.useMemo(() => {
    switch (payerTab) {
      case "private":
        return sortedLeads.filter(
          (l: any) => normalizePayerType(l?.payerType) === "PRIVATE"
        );
      case "non_private":
        return sortedLeads.filter(
          (l: any) => normalizePayerType(l?.payerType) !== "PRIVATE"
        );
      default:
        return sortedLeads;
    }
  }, [payerTab, sortedLeads]);

  const payerScopedInvites = React.useMemo(() => {
    switch (payerTab) {
      case "private":
        return providerInvites.filter(
          (invite) => getInvitePayerType(invite) === "PRIVATE"
        );
      case "non_private":
        return providerInvites.filter(
          (invite) => getInvitePayerType(invite) !== "PRIVATE"
        );
      default:
        return providerInvites;
    }
  }, [payerTab, providerInvites]);

  const scopedCounts = React.useMemo(() => {
    const all = payerScopedLeads.length + payerScopedInvites.length;
    const needsAction =
      payerScopedInvites.length +
      payerScopedLeads.filter((l: any) =>
        NEEDS_ACTION_STATUSES.has(String(l?.status || "").toUpperCase())
      ).length;
    const newlyReceived =
      payerScopedInvites.length +
      payerScopedLeads.filter(
        (l: any) => String(l?.status || "").toUpperCase() === "NEW"
      ).length;
    const placed = payerScopedLeads.filter(
      (l: any) => String(l?.status || "").toUpperCase() === "PLACED"
    ).length;
    const closed = payerScopedLeads.filter((l: any) =>
      CLOSED_LEAD_STATUSES.has(String(l?.status || "").toUpperCase())
    ).length;
    return { all, needsAction, newlyReceived, placed, closed };
  }, [payerScopedInvites.length, payerScopedLeads]);

  const filteredLeads = React.useMemo(() => {
    switch (viewFilter) {
      case "needs_action":
        return payerScopedLeads.filter((l: any) =>
          NEEDS_ACTION_STATUSES.has(String(l?.status || "").toUpperCase())
        );
      case "new":
        return payerScopedLeads.filter(
          (l: any) => String(l?.status || "").toUpperCase() === "NEW"
        );
      case "placed":
        return payerScopedLeads.filter(
          (l: any) => String(l?.status || "").toUpperCase() === "PLACED"
        );
      case "closed":
        return payerScopedLeads.filter((l: any) =>
          CLOSED_LEAD_STATUSES.has(String(l?.status || "").toUpperCase())
        );
      default:
        return payerScopedLeads;
    }
  }, [payerScopedLeads, viewFilter]);

  const filteredInvites = React.useMemo(() => {
    if (viewFilter === "placed" || viewFilter === "closed") return [];
    return payerScopedInvites;
  }, [payerScopedInvites, viewFilter]);

  React.useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [payerTab, viewFilter, leadsCount]);

  React.useEffect(() => {
    setViewFilter("all");
  }, [payerTab]);

  const visibleLeads = React.useMemo(
    () => filteredLeads.slice(0, visibleCount),
    [filteredLeads, visibleCount]
  );
  const hasMore = visibleCount < filteredLeads.length;
  const showProviderInvites = filteredInvites.length > 0;

  const handleInviteDialogClose = React.useCallback(
    () => setRespondDialog({ inviteId: null, action: null, businessId: null }),
    []
  );

  const handleInviteRespond = React.useCallback(
    async (note: string, agreementAccepted = false) => {
      if (!respondDialog.inviteId || !respondDialog.action) return;
      await respondToInvite({
        variables: {
          inviteId: respondDialog.inviteId,
          action: respondDialog.action,
          providerNote: note || null,
          agreementAccepted: respondDialog.action === "ACCEPTED" ? agreementAccepted : false,
        },
      });
      handleInviteDialogClose();
    },
    [handleInviteDialogClose, respondDialog.action, respondDialog.inviteId, respondToInvite]
  );

  const filterChip = (
    key: "needs_action" | "new" | "placed" | "closed" | "all",
    label: string,
    count: number
  ) => (
    <Chip
      size="small"
      clickable
      label={`${label} (${count})`}
      color={viewFilter === key ? "primary" : "default"}
      variant={viewFilter === key ? "filled" : "outlined"}
      onClick={() => setViewFilter(key)}
      sx={{ borderRadius: 999, fontWeight: 600 }}
    />
  );

  const renderReferralList = () => (
    <>
      <Tabs
        value={payerTab}
        onChange={(_e, next) => setPayerTab(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{
          mb: 1.4,
          minHeight: 48,
          "& .MuiTabs-flexContainer": {
            gap: 0.6,
          },
          "& .MuiTab-root": {
            minHeight: 40,
            px: 1.6,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 600,
            color: "text.secondary",
          },
          "& .Mui-selected": {
            color: "primary.main",
            backgroundColor: (t) => alpha(t.palette.primary.main, 0.08),
          },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
      >
        <Tab value="private" label={`Private (${counts.privateCount})`} />
        <Tab value="non_private" label={`Non-Private (${counts.nonPrivateCount})`} />
        <Tab value="all" label={`All (${counts.all})`} />
      </Tabs>
      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
        {filterChip("needs_action", "Needs Action", scopedCounts.needsAction)}
        {filterChip("new", "New", scopedCounts.newlyReceived)}
        {filterChip("placed", "Placed", scopedCounts.placed)}
        {filterChip("closed", "Closed", scopedCounts.closed)}
        {filterChip("all", "All", scopedCounts.all)}
      </Stack>
      {(showProviderInvites || filteredLeads.length > 0) ? (
        <Stack spacing={1.5}>
          {(inviteLoading || inviteError || respondInviteError || showProviderInvites) && (
            <Box sx={{ mb: 1.25 }}>
              {inviteLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
              {inviteError ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {String((inviteError as any)?.message || inviteError)}
                </Alert>
              ) : null}
              {respondInviteError ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {String((respondInviteError as any)?.message || respondInviteError)}
                </Alert>
              ) : null}
              {showProviderInvites ? (
                <Stack spacing={1}>
                  {filteredInvites.map((invite) => (
                    <ProviderInviteCard
                      key={invite.id}
                      invite={invite}
                      responding={respondingInvite}
                      onAccept={(inviteId) =>
                        respondToInvite({
                          variables: {
                            inviteId,
                            action: "ACCEPTED",
                            agreementAccepted: false,
                          },
                        })
                      }
                      onDecline={(inviteId, inviteBusinessId) =>
                        setRespondDialog({
                          inviteId,
                          action: "DECLINED",
                          businessId: inviteBusinessId ? String(inviteBusinessId) : null,
                        })
                      }
                    />
                  ))}
                </Stack>
              ) : null}
            </Box>
          )}
          {visibleLeads.map((lead) => (
            <ReferralItemAccordion
              key={lead.id}
              lead={lead}
              businessId={businessId}
              businessName={businessName}
              onOpenSettle={onOpenSettle}
              onCancelLead={onCancelLead}
              onAcceptNoAgreement={(leadId) =>
                shareProviderContact({ variables: { leadId: String(leadId) } })
              }
              onBeginInstallmentPay={onBeginInstallmentPay}
              cancelLoading={cancelLoading}
              acceptingNoAgreement={sharingProviderContact}
            />
          ))}
          {hasMore && (
            <Box sx={{ mt: 1.25, display: "flex", justifyContent: "center" }}>
              <Button variant="outlined" onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}>
                Show more ({filteredLeads.length - visibleLeads.length} remaining)
              </Button>
            </Box>
          )}
        </Stack>
      ) : (
        <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
          <Typography
            variant="overline"
            sx={{ display: "block", color: "text.secondary", mb: 0.5, letterSpacing: "0.08em" }}
          >
            Incoming Referrals
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "1rem" }}
          >
            {leadLoading
              ? "Loading referrals..."
              : "No referrals in this category."}
          </Typography>
        </Card>
      )}
      <ProviderInviteRespondDialog
        open={!!(respondDialog.inviteId && respondDialog.action)}
        action={respondDialog.action}
        businessId={respondDialog.businessId}
        onClose={handleInviteDialogClose}
        onSubmit={handleInviteRespond}
      />
    </>
  );

  const hasAnyIncoming = providerInvites.length > 0 || leads.length > 0;

  if (panelMode) {
    return (
      <Box>
        {leadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String((leadError as any)?.message || leadError)}
          </Alert>
        )}

        {hasAnyIncoming ? renderReferralList() : (
          <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
            <Typography
              variant="overline"
              sx={{ display: "block", color: "text.secondary", mb: 0.5, letterSpacing: "0.08em" }}
            >
              Incoming Referrals
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "1rem" }}
            >
              {leadLoading || inviteLoading
                ? "Loading referrals..."
                : "No referrals yet. Share your availability page or referral link to collect them."}
            </Typography>
          </Card>
        )}
      </Box>
    );
  }

  return (
    <Accordion defaultExpanded sx={{ mb: { xs: 1.5, sm: 2 } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          sx={{ width: "100%" }}
        >
          <Typography variant="h6">Incoming Referrals</Typography>
          <Chip
            size="small"
            label={leadsCount}
            variant="outlined"
            sx={{ ml: { sm: 0.5 } }}
          />

          {/* Push the action button to the right on wide screens */}
          <Box sx={{ flex: 1 }} />

          {/* ✅ Right-aligned “Manage Availability” button */}
          {availabilityManagementUrl && (
            <Tooltip title="Update your tour calendar and availability">
              <Button
                size="small"
                variant="contained"
                startIcon={<EventAvailableIcon />}
                href={availabilityManagementUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Update Tour Availability
              </Button>
            </Tooltip>
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        {leadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String((leadError as any)?.message || leadError)}
          </Alert>
        )}

        {hasAnyIncoming ? renderReferralList() : (
          <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "1rem" }}
            >
              {leadLoading || inviteLoading
                ? "Loading referrals..."
                : "No referrals yet. Share your availability page or referral link to collect them."}
            </Typography>
          </Card>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
